import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { ModuleThumbnail } from "@features/dashboard/components/ModuleThumbnail";
import { buttonConfig } from "@features/dashboard/constants";
import { getProethicaModuleInteractionState } from "@features/dashboard/helpers/proethica-module-list";
import { Box, useAppTheme } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import { Card, ProgressBar, Text } from "react-native-paper";
import type { DiagnosticCardProps, EXAM_STATUS } from "types/tag";

type Props = DiagnosticCardProps & {
	disabled?: boolean;
	isSessionConfig?: boolean;
	testKind?: ProethicaTestKind;
	hasAccess?: boolean;
	onPurchasePress?: () => void;
};

const getSessionMetadata = (subtitle: string) => {
	const [typeLabel, durationLabel] = subtitle.split(/\s*[•·]\s*/);

	return {
		typeLabel,
		durationLabel,
	};
};

const RecommendationSessionCard = ({
	title,
	subtitle,
	handleClickCard,
	icon,
	numItems,
	numCompleted,
	loading,
	disabled,
	button,
	isSessionConfig,
	thumbnailSource,
	testKind,
	hasAccess = true,
	status,
	onPurchasePress,
}: Props) => {
	const isCompleted = numItems > 0 && numCompleted >= numItems;
	const { needsPurchase, isCurriculumLocked } =
		getProethicaModuleInteractionState(hasAccess, disabled, isCompleted);
	const progress = numItems > 0 ? Math.min(numCompleted / numItems, 1) : 0;
	const isModuleCard = Boolean(thumbnailSource) || Boolean(testKind);

	const derivedStatus: EXAM_STATUS = isCompleted
		? "COMPLETE"
		: numCompleted > 0
			? "ACTIVE"
			: "NOT_STARTED";
	const { buttonMode, buttonIcon } =
		buttonConfig[status ?? derivedStatus] ?? buttonConfig.NOT_STARTED;
	const { typeLabel, durationLabel } = getSessionMetadata(subtitle);
	const renderedSubtitle = isModuleCard ? typeLabel : subtitle;
	const metadataChipProps = testKind ? { typeLabel } : { durationLabel };

	const theme = useAppTheme();
	return (
		<Card
			onPress={
				needsPurchase && onPurchasePress
					? onPurchasePress
					: isCurriculumLocked
						? undefined
						: handleClickCard
			}
			mode="outlined"
			style={{
				overflow: "hidden",
				opacity: isCurriculumLocked ? 0.5 : 1,
				borderRadius: theme.roundness,
				backgroundColor: theme.colors.surface,
				borderColor: theme.dark
					? theme.colors.outline
					: theme.colors.outlineVariant,
			}}
		>
			<Card.Content style={{ padding: 16 }}>
				<Box gap={isModuleCard ? 12 : 8}>
					<Box gap={12} flexDirection="row" alignItems="flex-start">
						{thumbnailSource ? (
							<ModuleThumbnail
								size={44}
								title={title}
								thumbnailSource={thumbnailSource}
								locked={isCurriculumLocked}
								isTest={Boolean(testKind)}
								testKind={testKind}
							/>
						) : testKind ? (
							<ModuleThumbnail
								size={44}
								title={title}
								locked={isCurriculumLocked}
								isTest
								testKind={testKind}
							/>
						) : (
							<Icon name={icon} size={24} color={theme.colors.onSurface} />
						)}
						<Box flex={1} minWidth={0}>
							<Text
								variant="titleSmall"
								numberOfLines={isModuleCard ? 2 : 1}
								style={{
									marginRight: isModuleCard ? 0 : 24,
									maxWidth: isSessionConfig ? 200 : undefined,
									fontWeight: "700",
									lineHeight: 20,
									minHeight: isModuleCard ? 40 : undefined,
								}}
							>
								{title}
							</Text>
							{isModuleCard ? null : (
								<Text
									variant="bodySmall"
									numberOfLines={1}
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{renderedSubtitle}
								</Text>
							)}
						</Box>
					</Box>
					<ProgressBar
						style={{
							height: 8,
							marginHorizontal: isModuleCard ? 4 : 0,
						}}
						progress={progress}
					/>
				</Box>
			</Card.Content>
			{button && (
				<Card.Actions
					style={{
						paddingHorizontal: 16,
						paddingTop: isModuleCard ? 0 : 8,
						paddingBottom: 16,
						justifyContent: isModuleCard ? "space-between" : "flex-end",
						alignItems: isModuleCard ? "flex-end" : "center",
					}}
				>
					{isModuleCard ? <ModuleMetadataChips {...metadataChipProps} /> : null}
					<ModuleActionButton
						mode={
							needsPurchase
								? "contained-tonal"
								: isModuleCard
									? buttonMode
									: "text"
						}
						icon={
							needsPurchase
								? "lock-outline"
								: isModuleCard
									? buttonIcon
									: button.icon
						}
						cta={button.cta}
						onPress={
							needsPurchase && onPurchasePress
								? onPurchasePress
								: button.handleClick
						}
						disabled={needsPurchase ? false : isCurriculumLocked}
						loading={loading}
						style={isModuleCard ? { alignSelf: "flex-end" } : undefined}
					/>
				</Card.Actions>
			)}
		</Card>
	);
};

export default RecommendationSessionCard;
